import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import api from "@/lib/api";
import { toast } from "sonner";
import { TIDS } from "@/constants/testIds";
import { Wallet, ShieldCheck, Banknote, IndianRupee, ArrowUpRight, CheckCircle2 } from "lucide-react";

const inr = (n) => "₹" + Number(n || 0).toLocaleString("en-IN", { maximumFractionDigits: 2 });

export default function Payouts() {
  const [summary, setSummary] = useState(null);
  const [ledger, setLedger] = useState(null);
  const [ledgerLoading, setLedgerLoading] = useState(false);
  const [history, setHistory] = useState([]);
  const [bank, setBank] = useState({ holder_name: "", account_number: "", ifsc: "", pan: "", upi: "" });
  const [bankExists, setBankExists] = useState(false);
  const [isAdmin, setIsAdmin] = useState(false);
  const [busy, setBusy] = useState(false);

  const load = async () => {
    const [{ data: s }, { data: l }, { data: h }, { data: b }, { data: a }] = await Promise.all([
      api.get("/payouts/summary"),
      api.get("/payouts/ledger?limit=50&skip=0"),
      api.get("/payouts"),
      api.get("/payouts/bank"),
      api.get("/payouts/is-admin"),
    ]);
    setSummary(s); setLedger(l); setHistory(h);
    if (b.exists) {
      setBank({
        holder_name: b.holder_name || "",
        account_number: b.account_number || "",
        ifsc: b.ifsc || "",
        pan: b.pan || "",
        upi: b.upi || "",
      });
      setBankExists(true);
    }
    setIsAdmin(a.is_admin);
  };
  useEffect(() => { load(); }, []);

  const loadMore = async () => {
    if (!ledger || !ledger.pagination?.has_more) return;
    setLedgerLoading(true);
    try {
      const { data } = await api.get(`/payouts/ledger?limit=50&skip=${ledger.entries.length}`);
      setLedger({
        ...data,
        entries: [...ledger.entries, ...data.entries],
      });
    } catch (e) { toast.error(e.response?.data?.detail || e.message); }
    finally { setLedgerLoading(false); }
  };

  const saveBank = async () => {
    if (!bank.holder_name || !bank.account_number || !bank.ifsc) {
      toast.error("Holder name, account number, and IFSC are required");
      return;
    }
    setBusy(true);
    try {
      await api.put("/payouts/bank", bank);
      setBankExists(true);
      toast.success("Bank details saved");
    } catch (e) { toast.error(e.response?.data?.detail || e.message); }
    finally { setBusy(false); }
  };

  return (
    <div className="space-y-8 vp-fade-up">
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div>
          <div className="vp-label">Payouts</div>
          <h1 className="font-display text-3xl font-bold mt-1">Money in. Money out.</h1>
          <p className="text-[#4A4D57] mt-2 text-sm max-w-xl">Your earnings from paid webinars & cohorts, minus the platform fee. We settle payouts manually to your bank account.</p>
        </div>
        {isAdmin && (
          <Link to="/app/admin/payouts" className="vp-btn-ghost text-sm inline-flex items-center gap-2">
            <ShieldCheck className="w-4 h-4" /> Admin panel
          </Link>
        )}
      </div>

      {/* Summary cards */}
      {summary && (
        <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-4" data-testid={TIDS.payoutsSummary}>
          <SumCard label="Payable now" value={inr(summary.payable_now)} icon={IndianRupee} accent="#0B5CFF" primary />
          <SumCard label="Lifetime gross" value={inr(summary.lifetime_gross)} icon={ArrowUpRight} />
          <SumCard label={`Platform fee (${summary.platform_fee_pct}%)`} value={inr(summary.lifetime_platform_fee)} icon={Wallet} />
          <SumCard label="Lifetime paid out" value={inr(summary.lifetime_paid_out)} icon={CheckCircle2} />
        </div>
      )}

      {/* Bank details */}
      <div className="vp-card p-6">
        <div className="flex items-center gap-2 mb-1">
          <Banknote className="w-4 h-4 text-[#0B5CFF]" />
          <div className="vp-label">Bank details</div>
        </div>
        <p className="text-sm text-[#4A4D57] mb-4">Where should we send your payouts? Update anytime — changes apply to future payouts only.</p>
        <div className="grid sm:grid-cols-2 gap-3">
          <TextField label="Account holder name" value={bank.holder_name} onChange={(v) => setBank({ ...bank, holder_name: v })} testid={TIDS.payoutsBankHolder} required />
          <TextField label="Account number" value={bank.account_number} onChange={(v) => setBank({ ...bank, account_number: v })} testid={TIDS.payoutsBankAccount} required />
          <TextField label="IFSC code" value={bank.ifsc} onChange={(v) => setBank({ ...bank, ifsc: v.toUpperCase() })} testid={TIDS.payoutsBankIfsc} required />
          <TextField label="PAN (recommended)" value={bank.pan} onChange={(v) => setBank({ ...bank, pan: v.toUpperCase() })} testid={TIDS.payoutsBankPan} />
          <TextField label="UPI ID (optional)" value={bank.upi} onChange={(v) => setBank({ ...bank, upi: v })} testid={TIDS.payoutsBankUpi} />
        </div>
        <div className="mt-4 flex items-center gap-3">
          <button data-testid={TIDS.payoutsBankSaveBtn} onClick={saveBank} disabled={busy} className="vp-btn-primary text-sm">
            {busy ? "Saving…" : bankExists ? "Update details" : "Save details"}
          </button>
          {bankExists && <span className="text-xs text-[#4A4D57] inline-flex items-center gap-1"><CheckCircle2 className="w-3.5 h-3.5 text-[#1E8E3E]" /> Saved</span>}
        </div>
      </div>

      {/* Ledger */}
      {ledger && (
        <div className="vp-card p-6">
          <div className="flex items-center justify-between mb-3">
            <div>
              <div className="vp-label">Payment ledger</div>
              <h2 className="font-display font-bold text-xl mt-1">Every paid transaction ({ledger.totals.count})</h2>
            </div>
            <div className="text-right text-xs text-[#4A4D57]">
              Gross · {inr(ledger.totals.gross)} · Fee · {inr(ledger.totals.platform_fee)} · Net · <span className="text-[#0B5CFF] font-semibold">{inr(ledger.totals.net)}</span>
            </div>
          </div>
          {ledger.entries.length === 0 ? (
            <div className="text-sm text-[#747680] text-center py-8">No paid transactions yet. Publish a paid webinar to start earning.</div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="text-left text-xs text-[#747680] uppercase tracking-wide border-b border-[#E4E6EC]">
                    <th className="py-2 pr-3">When</th>
                    <th className="py-2 pr-3">Kind</th>
                    <th className="py-2 pr-3">Customer</th>
                    <th className="py-2 pr-3 text-right">Gross</th>
                    <th className="py-2 pr-3 text-right">Fee</th>
                    <th className="py-2 pr-3 text-right">Net</th>
                  </tr>
                </thead>
                <tbody>
                  {ledger.entries.map((e) => (
                    <tr key={e.id} data-testid={TIDS.payoutsLedgerRow} className="border-b border-[#F1F3F7] last:border-0">
                      <td className="py-2 pr-3 text-[#4A4D57]">{(e.paid_at || "").slice(0, 10)}</td>
                      <td className="py-2 pr-3"><span className="vp-chip">{e.kind}</span></td>
                      <td className="py-2 pr-3">
                        <div className="font-medium text-[#0B0D17]">{e.name}</div>
                        <div className="text-xs text-[#747680]">{e.email}</div>
                      </td>
                      <td className="py-2 pr-3 text-right">{inr(e.gross)}</td>
                      <td className="py-2 pr-3 text-right text-[#B00020]">-{inr(e.platform_fee)}</td>
                      <td className="py-2 pr-3 text-right font-semibold text-[#0B5CFF]">{inr(e.net)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
              {ledger.pagination?.has_more && (
                <div className="mt-4 text-center">
                  <button onClick={loadMore} disabled={ledgerLoading} className="vp-btn-ghost text-sm">
                    {ledgerLoading ? "Loading…" : `Load more (${ledger.totals.count - ledger.entries.length} remaining)`}
                  </button>
                </div>
              )}
            </div>
          )}
        </div>
      )}

      {/* Payout history */}
      <div className="vp-card p-6">
        <div className="vp-label">Payout history</div>
        <h2 className="font-display font-bold text-xl mt-1">When money left our bank for yours</h2>
        {history.length === 0 ? (
          <div className="text-sm text-[#747680] text-center py-8 mt-4">No payouts yet. Your first payout will appear here after admin processes it.</div>
        ) : (
          <div className="overflow-x-auto mt-4">
            <table className="w-full text-sm">
              <thead>
                <tr className="text-left text-xs text-[#747680] uppercase tracking-wide border-b border-[#E4E6EC]">
                  <th className="py-2 pr-3">Paid at</th>
                  <th className="py-2 pr-3">Method</th>
                  <th className="py-2 pr-3">Reference</th>
                  <th className="py-2 pr-3">Notes</th>
                  <th className="py-2 pr-3 text-right">Amount</th>
                  <th className="py-2 pr-3">Status</th>
                </tr>
              </thead>
              <tbody>
                {history.map((p) => (
                  <tr key={p.id} data-testid={TIDS.payoutsHistoryRow} className="border-b border-[#F1F3F7] last:border-0">
                    <td className="py-2 pr-3 text-[#4A4D57]">{(p.paid_at || p.created_at || "").slice(0, 10)}</td>
                    <td className="py-2 pr-3"><span className="vp-chip">{p.method}</span></td>
                    <td className="py-2 pr-3 font-mono text-xs">{p.reference || "—"}</td>
                    <td className="py-2 pr-3 text-[#4A4D57]">{p.notes || "—"}</td>
                    <td className="py-2 pr-3 text-right font-semibold">{inr(p.amount)}</td>
                    <td className="py-2 pr-3">
                      <span className={"vp-chip " + (p.status === "paid" ? "vp-chip-accent" : "")}>{p.status}</span>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
}

function SumCard({ label, value, icon: Icon, primary }) {
  return (
    <div className={"vp-card p-5 " + (primary ? "border-[#0B5CFF]/50 bg-[#0B5CFF]/6" : "")}>
      <div className="flex items-center gap-2 text-[#4A4D57]">
        <Icon className="w-4 h-4" strokeWidth={1.75} />
        <div className="text-xs uppercase tracking-wide">{label}</div>
      </div>
      <div className={"font-display font-black text-3xl mt-2 " + (primary ? "text-[#0B5CFF]" : "text-[#0B0D17]")}>{value}</div>
    </div>
  );
}

function TextField({ label, value, onChange, testid, required }) {
  return (
    <label className="block">
      <span className="vp-label mb-1.5 block" style={{ fontSize: "0.65rem" }}>{label}{required && " *"}</span>
      <input data-testid={testid} className="vp-input text-sm" value={value || ""} onChange={(e) => onChange(e.target.value)} />
    </label>
  );
}
